import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Chip,
  Tooltip,
} from '@mui/material';

import TableCells from 'src/components/Tables/TableCells';

import CheckboxInputs from 'src/components/inputs/CheckboxInputs';
import { useMain } from '../hooks/useMain';
import TextInputs from 'src/components/inputs/TextInputs';
import RadioInputs from 'src/components/inputs/RadioInputs';
import { ReqField } from 'src/components/CustomComponents/CustomLoader';
import SelectInputs from 'src/components/inputs/SelectInputs';
import ShowLoader from 'src/components/CustomComponents/ShowLoader';
import { MdDelete } from "react-icons/md";
import { FaRegEdit } from "react-icons/fa";
import { Whatshot, WbSunny, AcUnit, SpeakerNotes } from '@mui/icons-material';
import { getHighlightedText } from 'src/utils/textUtils';

const enquiryType = [
  { label: 'Hot', value: 'Hot' },
  { label: 'Cold', value: 'Cold' },
  { label: 'Warm', value: 'Warm' },
];

const sourceOfEnquiry = [
  { label: 'Twitter', value: 'Twitter' },
  { label: 'Instagram', value: 'Instagram' },
  { label: 'Walking', value: 'Walking' },
  { label: 'Facebook', value: 'Facebook' },
  { label: 'Website', value: 'Website' },
];

const leadStatus = [
  { label: 'Pending', value: 'Pending' },
  { label: 'Converted', value: 'Converted' },
  { label: 'Closed', value: 'Closed' },
];

const FieldData = ({ data, setData, getData, checkedClientEnquiry, setCheckedClientEnquiry, search }) => {
  const { activeBranchId, getBranch, updateClientEnquiry, deleteClientEnquiry, setShowMessage } =
    useMain();

  const [formData, setFormData] = useState({});
  const [originalResponses, setOriginalResponses] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [enquiryFor, setEnquiryFor] = useState([]);
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [openDialogue, setOpenDialogue] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleCloseDialogue = () => {
    setOpenDialogue(false);
    setOriginalResponses([]);
    setFormData({});
    setSelectedItemId(null);
  };

  const handleUpdate = (item) => {
    const { branchDetails, createdAt, sendSmsAs, updatedAt, __v, _id, ...rest } = item;
    const normalizedResponses = Array.isArray(rest.clientResponse) ? rest.clientResponse : (rest.clientResponse ? [rest.clientResponse] : []);
    setOriginalResponses(normalizedResponses);
    setFormData({ 
      ...rest, 
      dateOfFollow: convertIsoDateToYYYYMMDD(rest?.dateOfFollow),
      clientResponse: []
    });
    setSelectedItemId(item._id);
    setOpenDialogue(true);
  };

  const handleUpdateEnquiry = async () => {
    setLoading(true);
    try {
      const { dateOfFollow, availability, ...formDataWithoutDateAndAvailability } = formData;
      const cleanedResponses = (formData.clientResponse || [])
        .map(r => (typeof r === 'string' ? r.trim() : r))
        .filter(r => r !== '');

      const res = await updateClientEnquiry(
        selectedItemId,
        {
          ...formDataWithoutDateAndAvailability,
          clientResponse: cleanedResponses
        },
        `?branchId=${activeBranchId}`,
      );
      if (res?.statusCode === 200) {
        setShowMessage({ message: res.message || 'Enquiry Updated', messageType: 'success' });
        setData([]);
        getData();
      } else {
        setShowMessage({ message: res.message || 'Enquiry not updated', messageType: 'error' });
      }
    } catch (error) {
      setShowMessage({ message: error.message || 'Something Went Wrong', messageType: 'error' });
    } finally {
      setLoading(false);
      setOpenDialogue(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((pre) => ({ ...pre, [name]: value }));
  };

  const handleDeleteField = (id) => {
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      const res = await deleteClientEnquiry(deleteId, `?branchId=${activeBranchId}`);
      if (res?.statusCode === 200) {
        setShowMessage({
          message: res.message || 'Enquiry deleted successfully',
          messageType: 'success',
        });
        setData(prevData => prevData.filter((d) => d._id !== deleteId));
        await getData();
      } else {
        setShowMessage({
          message: res.message || 'Enquiry not deleted',
          messageType: 'error',
        });
      }
    } catch (error) {
      setShowMessage({
        message: error.message || 'Something went wrong',
        messageType: 'error',
      });
    } finally {
      setOpenDeleteDialog(false);
      setDeleteId(null);
      setDeleteLoading(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteId(null);
  };

  const handleAllCheckboxChange = () => {
    if (isAllChecked) {
      setCheckedClientEnquiry([]);
    } else {
      setCheckedClientEnquiry(data);
    }
    setIsAllChecked(!isAllChecked);
  };

  const handleSignleCheckboxChange = (clientEnquiryId) => {
    if (
      checkedClientEnquiry.length &&
      checkedClientEnquiry.some((check) => check._id === clientEnquiryId)
    ) {
      setCheckedClientEnquiry((prev) => prev.filter((check) => check._id !== clientEnquiryId));
    } else {
      const checked = data.filter((item) => item._id === clientEnquiryId);
      setCheckedClientEnquiry((prev) => [...prev, ...checked]);
    }
  };

  useEffect(() => {
    setIsAllChecked((data?.length && checkedClientEnquiry?.length === data?.length) || false);
  }, [checkedClientEnquiry?.length, data?.length]);

  const getAllData = async () => {
    try {
      const res = await getBranch(activeBranchId);

      const products = res?.data?.products || [];
      const services = res?.data?.services || [];

      const productOptions = products.map((product) => ({
        label: product.productName,
        value: product.productName,
      }));

      const serviceOptions = services.map((service) => ({
        label: service.serviceName,
        value: service.serviceName,
      }));

      setEnquiryFor([...productOptions, ...serviceOptions]);
    } catch (error) { }
  };

  useEffect(() => {
    if (activeBranchId) {
      getAllData();
    }
  }, [activeBranchId]);
const enquiryGridTemplate = '40px 1fr 0.9fr 0.6fr 0.5fr 0.9fr 0.8fr 0.8fr 1fr 1fr 0.8fr 0.9fr 0.8fr'; 
  const enquiryGridRowStyle = {
    display: 'grid',
    gridTemplateColumns: enquiryGridTemplate,
    alignItems: 'center',
    columnGap: '4px',
  };
  return (
    <Box sx={{ overflow: 'auto', width: { xs: '450px', sm: 'auto' } }}>
      <table className="table-container mt-3">
        <thead className="table-thead">
          <tr style={enquiryGridRowStyle}>
            <th style={{ padding: '8px' }}>
              <CheckboxInputs
                type={'Heading'}
                isAllChecked={isAllChecked}
                handleAllCheckboxChange={handleAllCheckboxChange}
              />
            </th>
            {[
              'Name',
              'Phone',
              'Gender',
              'Age',
              'Date to follow',
              'Enquiry Type',
              'Lead Status',
              'Enquiry for',
              'Source Of Enquiry',
              'Response',
              'Reference',
              'Actions',
            ].map((h, index) => {
              return (
               <th key={index} style={{ padding: '8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {h}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="table-tbody">
          {data.map((item) => {
            const originalDate = item?.dateOfFollow ? convertIsoDateToYYYYMMDD(item?.dateOfFollow) : '';

            let formattedFollowDate = '';
            if (originalDate) {
              const [year, month, day] = originalDate.split('-');
              formattedFollowDate = `${day}-${month}-${year}`;
            }
            return (
              <tr key={item._id} style={enquiryGridRowStyle}>
                  <td style={{ padding: '8px' }}>
                  <CheckboxInputs
                    type={'content'}
                    handleSignleCheckboxChange={handleSignleCheckboxChange}
                    checkedItems={checkedClientEnquiry}
                    fieldId={item?._id}
                  />
                </td>
                <td style={{ width: '100%' , textAlign: 'center'}}>{getHighlightedText(item?.clientName, search)}</td>
                <td style={{ width: '100%', textAlign: 'center'}}>{getHighlightedText(item?.clientNumber, search)}</td>
                <td style={{ width: '100%' , textAlign: 'center'}}>{item?.gender}</td>
                <td style={{ width: '100%' , textAlign: 'center'}}>{item?.age || '-'}</td>
                <td style={{ width: '100%' , textAlign: 'center'}}>{formattedFollowDate}</td>
                <td style={{ width: '100%' , textAlign: 'center'}}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                    {item?.enquiryType === 'Hot' && <Whatshot sx={{ color: '#f44336', fontSize: '1.1rem' }} />}
                    {item?.enquiryType === 'Warm' && <WbSunny sx={{ color: '#ff9800', fontSize: '1.1rem' }} />}
                    {item?.enquiryType === 'Cold' && <AcUnit sx={{ color: '#2196f3', fontSize: '1.1rem' }} />}
                    <Typography variant="body2" fontWeight="600">{item?.enquiryType}</Typography>
                  </Box>
                </td>
                <td style={{ width: '100%' , textAlign: 'center'}}>{item?.leadStatus}</td>
                <td style={{ width: '100%' , textAlign: 'center'}}>{item?.enquiryFor}</td>
                <td style={{ width: '100%' , textAlign: 'center'}}>{item?.sourceOfEnquiry}</td>
                <td style={{ width: '100%' , textAlign: 'center'}}>
                  {item?.clientResponse?.length > 0 ? (
                    <Tooltip
                      title={
                        <Box sx={{ p: 0.5 }}>
                          {item.clientResponse.map((resp, index) => (
                            <Typography key={index} variant="caption" display="block" sx={{ borderBottom: index !== item.clientResponse.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none', pb: 0.5, mb: 0.5 }}>
                              <b>{index + 1}.</b> {resp}
                            </Typography>
                          ))}
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <IconButton size="small" sx={{ color: '#6174DD' }}>
                        <SpeakerNotes sx={{ fontSize: '1.2rem' }} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    '-'
                  )}
                </td>
                <td style={{ width: '100%' , textAlign: 'center'}}>{item?.reference || '-'}</td>
                <td style={{
                  width: '100%',
                  textAlign: 'center',
                }}>
                  <IconButton onClick={() => handleUpdate(item)} color="secondary" title="Update" style={{ marginRight: '24px' }}>
                    <FaRegEdit style={{ color: '#696969', fontSize: '1rem' }} />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteField(item?._id)} color="error" title="Delete" style={{ marginLeft: '-33px' }}>
                    <MdDelete color='#FF4D4D' style={{ fontSize: '1.2rem', marginTop: '1px' }} />
                  </IconButton>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogContent>
          Are you sure you want to delete this Enquiry?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">Cancel</Button>
          <Button onClick={confirmDelete} color="secondary">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Styled Section Logic for Dialog */}
      {/* (Adding this helper locally for the dialog content) */}
      
      <Dialog open={openDialogue} onClose={handleCloseDialogue} maxWidth="md">
        <DialogTitle>Update Enquiry Details</DialogTitle>
        <DialogContent>
          <DialogContentText>Modify the enquiry information and track client interactions below.</DialogContentText>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            
            {/* Section 1: Client Info */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ color: '#6174DD', fontWeight: 700, borderBottom: '1px solid #e2e8f0', pb: 0.5, mb: 1 }}>
                CLIENT INFORMATION
              </Typography>
            </Grid>
            <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextInputs
                  name="clientNumber"
                  title="Contact number"
                  placeholder="Contact Number"
                  type="number"
                  value={formData?.clientNumber}
                  disabled={true}
                  handleChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextInputs
                  name="clientName"
                  title="Client name"
                  placeholder="Client name"
                  type="text"
                  value={formData?.clientName}
                  handleChange={handleInputChange}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={3}>
                <TextInputs
                  name="age"
                  title="Age"
                  placeholder="Age"
                  required={false}
                  type="number"
                  value={formData?.age}
                  handleChange={handleInputChange}
                />
              </Grid>
              <Grid
                item
                xs={3}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <RadioInputs
                  title={<>Gender <ReqField /></>}
                  name={'gender'}
                  value={formData?.gender}
                  handleChange={handleInputChange}
                  options={[
                    { title: 'M', value: 'Male' },
                    { title: 'F', value: 'Female' },
                  ]}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={3}>
                <TextInputs
                  name="address"
                  title="Area/City"
                  placeholder="Area/City"
                  type="text"
                  required={false}
                  value={formData?.address}
                  handleChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={3}>
                <SelectInputs title="Source of enquiry" name="sourceOfEnquiry" value={formData?.sourceOfEnquiry} handleChange={handleInputChange} options={sourceOfEnquiry} optionValue="value" optionTitle="label" disabled={true} />
              </Grid>
            </Grid>
            </Grid>

            {/* Section 2: Enquiry Details */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#6174DD', fontWeight: 700, borderBottom: '1px solid #e2e8f0', pb: 0.5, mb: 1 }}>
                ENQUIRY DETAILS
              </Typography>
            </Grid>
            <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="subtitle1" fontWeight={550} component="label" htmlFor="enquiryFor" mb="4px">
                  Enquiry for <ReqField />
                </Typography>
                <Autocomplete
                  id="enquiryFor"
                  name="enquiryFor"
                  options={enquiryFor}
                  value={formData && formData.enquiryFor ? enquiryFor.find((option) => option.value === formData.enquiryFor) : null}
                  onChange={(event, result) =>
                    handleInputChange({ target: { name: 'enquiryFor', value: result?.value } })
                  }
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  getOptionLabel={(option) => option.label}
                  renderInput={(params) => <TextField {...params} type="text" variant="outlined" placeholder="Services / Products" />}
                />
              </Grid>
              <Grid item xs={6}>
                <TextInputs name="leadRepresentative" title="Lead representative" type="text" placeholder="representative" required={false} value={formData?.leadRepresentative} handleChange={handleInputChange} />
              </Grid>
              <Grid item xs={3}>
                <SelectInputs title="Enquiry type" name="enquiryType" value={formData?.enquiryType} handleChange={handleInputChange} options={enquiryType} optionValue="value" optionTitle="label" />
              </Grid>
            </Grid>
            </Grid>

            {/* Section 3: Interaction Responses */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#6174DD', fontWeight: 700, borderBottom: '1px solid #e2e8f0', pb: 0.5, mb: 1 }}>
                INTERACTION HISTORY
                </Typography>
              </Grid>

              {/* Historical Responses (Read-Only Fields) */}
              {originalResponses.map((resp, index) => (
                <Grid item xs={3} key={`history-${index}`}>
                  <Typography variant="subtitle1" fontWeight={550} component="label" mb="4px" color="text.secondary">
                    Response {index + 1} (History)
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={resp}
                    disabled
                    sx={{
                      '& .MuiInputBase-root': { bgcolor: '#f8fafc', fontSize: '0.85rem' }
                    }}
                  />
                </Grid>
              ))}

              {/* New Editable Responses */}
              {(formData?.clientResponse || []).map((resp, index) => (
                <Grid item xs={3} key={`new-${index}`}>
                  <Typography variant="subtitle1" fontWeight={550} component="label" mb="4px" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Response {originalResponses.length + index + 1}
                    <IconButton
                      size="small"
                      onClick={() => {
                        const newResponses = (formData.clientResponse || []).filter((_, i) => i !== index);
                        setFormData(prev => ({ ...prev, clientResponse: newResponses }));
                      }}
                      sx={{ color: '#FF4D4D', p: '2px' }}
                    >
                      <MdDelete style={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Type client response..."
                    value={resp}
                    onChange={(e) => {
                      const newResponses = [...(formData.clientResponse || [])];
                      newResponses[index] = e.target.value;
                      setFormData(prev => ({ ...prev, clientResponse: newResponses }));
                    }}
                    sx={{
                      '& .MuiInputBase-root': { fontSize: '0.85rem' }
                    }}
                  />
                </Grid>
              ))}

              <Grid item xs={3} sx={{ display: 'flex', alignItems: 'flex-end', pb: '4px' }}>
                <Button
                  variant="outlined"
                  fullWidth
                  size="small"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      clientResponse: [...(prev.clientResponse || []), '']
                    }));
                  }}
                  sx={{
                    height: '40px',
                    textTransform: 'none',
                    borderColor: '#6174DD',
                    color: '#6174DD',
                    fontWeight: 600,
                    '&:hover': { borderColor: '#4a5ec4', bgcolor: 'rgba(97, 116, 221, 0.04)' }
                  }}
                >
                  + Add More Response
                </Button>
              </Grid>
            </Grid>

            {/* Section 4: Tracking */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#6174DD', fontWeight: 700, borderBottom: '1px solid #e2e8f0', pb: 0.5, mb: 1 }}>
                STATUS & FOLLOW-UP
              </Typography>
            </Grid>
            <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <TextInputs name="dateOfFollow" title="Follow-up Date" type="Date" value={formData?.dateOfFollow} handleChange={handleInputChange} disabled={true} />
              </Grid>
              <Grid item xs={3}>
                <TextInputs name="reference" title="Reference" placeholder="Reference" type="text" required={false} value={formData?.reference} handleChange={handleInputChange} />
              </Grid>
              <Grid item xs={3}>
                <SelectInputs title="Lead status" name="leadStatus" value={formData?.leadStatus} handleChange={handleInputChange} options={leadStatus} optionValue="value" optionTitle="label" />
              </Grid>
            </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
          {loading ? (
            <ShowLoader />
          ) : (
            <>
              <div style={{ width: '200px', margin: '2rem auto' }}>
                <Button
                  variant="contained"
                  fullWidth
                  color="primary"
                  sx={{
                    background: '#6174DD',
                  }}
                  onClick={handleCloseDialogue}
                >
                  Cancel
                </Button>
              </div>
              <div style={{ width: '200px', margin: '3rem auto' }}>
                <Button
                  variant="contained"
                  fullWidth
                  color="primary"
                  sx={{
                    background: '#6174DD',
                  }}
                  onClick={handleUpdateEnquiry}
                >
                  Update
                </Button>
              </div>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FieldData;

function convertIsoDateToYYYYMMDD(isoDate) {
  const dateObject = new Date(isoDate);
  const year = dateObject.getFullYear();
  const month = (dateObject.getMonth() + 1).toString().padStart(2, '0');
  const day = dateObject.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
}
